import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import HospitalMarquee from "@/components/HospitalMarquee";
import {
  ArrowLeft, ChevronRight, Calendar, MessageCircle, Phone, GraduationCap,
  Scissors, Heart, Shield, Users, MapPin, Building2, Microscope, Sparkles,
  Target, Brain, HandHeart, Stethoscope, Award, Activity
} from "lucide-react";

const expertise = {
  cancers: [
    "Ovarian Cancer (Early & Advanced Stage)",
    "Cervical Cancer",
    "Uterine (Endometrial) Cancer",
    "Vulval & Vaginal Cancer",
    "Rare Gynecologic Malignancies",
  ],
  surgical: [
    "Major Cytoreductive (Debulking) Surgeries",
    "HIPEC (Hyperthermic Intraperitoneal Chemotherapy)",
    "Laparoscopic Cancer Surgery",
    "Robotic-Assisted Gynecologic Surgery",
    "Pelvic Exenteration (Complex Cancer Surgery)",
  ],
  benign: [
    "Endometriosis (Deep Infiltrating Endometriosis)",
    "Ovarian Cysts",
    "Uterine Fibroids (Tumors)",
  ],
  preventive: [
    "Cancer Screening (Pap Smear, HPV Testing)",
    "High-Risk Cancer Evaluation",
    "Preventive Oncology & Early Detection Programs",
  ],
};

const clinicalPrinciples = [
  { icon: Target, text: "Early and accurate diagnosis" },
  { icon: Brain, text: "Evidence-based treatment planning" },
  { icon: Scissors, text: "Minimally invasive surgery whenever possible" },
  { icon: Heart, text: "Organ and fertility preservation (when safe and appropriate)" },
  { icon: Users, text: "Multidisciplinary cancer care coordination" },
  { icon: HandHeart, text: "Compassionate emotional support throughout treatment" },
];

const hospitals = [
  "Manipal Hospitals, EM Bypass (formerly Medica Superspecialty Hospital)",
  "Manipal Hospitals, Mukundapur",
  "Manipal Hospitals, Broadway",
  "Manipal Hospitals, Saltlake",
];

const philosophyPoints = [
  "Clear communication and patient education",
  "Transparent treatment planning",
  "Emotional support for patients and families",
  "Focus on quality of life and long-term outcomes",
];

const patientExperience = [
  "Clear and easy to understand medical explanations",
  "Supportive and empathetic consultation experience",
  "Structured and well-planned treatment pathways",
  "High level of surgical confidence and expertise",
];

const areasServed = [
  "Mukundapur", "EM Bypass", "Salt Lake", "Phoolbagan",
  "Kankurgachi", "Garia", "Kasba", "Ballygunge", "South Kolkata",
];

const stats = [
  { value: "10+", label: "Years of Experience" },
  { value: "1000+", label: "Complex Cancer Cases" },
  { value: "4", label: "Hospital Affiliations" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55 } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

export default function HospitalsPage() {
  return (
    <>
      <Helmet>
        <title>
          Dr. Neha Agarwal – Gynecologic Oncologist in Kolkata | Cancer & Robotic Surgeon
        </title>

        <meta
          name="description"
          content="Dr. Neha Agarwal is a leading gynecologic oncologist in Kolkata specializing in ovarian, cervical, uterine cancer, HIPEC, robotic and laparoscopic cancer surgery."
        />
      </Helmet>
      <div className="bg-[#F9F9F6] min-h-screen">


        {/* ─── HOSPITAL AFFILIATIONS ─── */}
        <section className="pt-40 md:pt-40 bg-[#F2EFE9]" data-testid="about-hospitals-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              className="mb-12"
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Hospital Affiliations
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-2xl" data-testid="about-hospitals-heading">
                Associated Hospitals in Kolkata
              </h2>
              <p className="text-base sm:text-lg text-[#4A6359] mt-4 max-w-2xl leading-relaxed">
                Dr. Neha Agarwal is associated with leading cancer care hospitals in Kolkata, ensuring access to advanced infrastructure and multidisciplinary oncology support. These centers provide state-of-the-art facilities for cancer surgery, chemotherapy, HIPEC, and post-operative care.            </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={stagger}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              {hospitals.map((h, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`about-hospital-${i}`}
                  className="flex items-center gap-4 bg-[#F9F9F6] rounded-2xl p-6 border border-[#D8DDD9] hover:-translate-y-1 hover:shadow-lg hover:shadow-[#1E3F33]/5 transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#1E3F33]/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-[#1E3F33]" />
                  </div>
                  <span className="text-base font-medium text-[#1E3F33]">{h}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
    <HospitalMarquee />
       {/* ─── FINAL CTA ─── */}
            <section className="py-20 md:pb-28 bg-[#F9F9F6]" data-testid="about-cta-section">
              <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-80px" }}
                  variants={fadeUp}
                >
                  <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-2xl mx-auto mb-4" data-testid="about-cta-heading">
                    Book Consultation
                  </h2>
                  <p className="text-base sm:text-lg text-[#4A6359] max-w-lg mx-auto leading-relaxed mb-8">
                    If you are looking for expert care for gynecologic cancer or complex gynecologic conditions, early consultation is strongly recommended.
                  </p>
                  <div className="flex flex-wrap justify-center gap-4">
                    <Link
                      to="/book-appointment"
                      data-testid="about-cta-book-btn"
                      className="inline-flex items-center gap-2 bg-[#1E3F33] text-[#F9F9F6] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#2C5545] transition-colors"
                    >
                      <Calendar className="w-4 h-4" />
                      Book Appointment
                    </Link>
                    <a
                      href="tel:+916291000829"
                      data-testid="about-cta-call-btn"
                      className="inline-flex items-center gap-2 border-2 border-[#1E3F33] text-[#1E3F33] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#1E3F33] hover:text-[#F9F9F6] transition-all"
                    >
                      <Phone className="w-4 h-4" />
                      Get Second Opinion
                    </a>
                    <a
                      href="https://wa.me/916291000829"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="about-cta-whatsapp-btn"
                      className="inline-flex items-center gap-2 text-[#4A6359] px-5 py-4 text-sm font-semibold hover:text-[#1E3F33] transition-colors underline underline-offset-4 decoration-[#8FA998]"
                    >
                      <MessageCircle className="w-4 h-4" />
                      WhatsApp Consultation
                    </a>
                  </div>
                </motion.div>
              </div>
            </section>
       {/* ─── FOOTER ─── */}
        {/* <footer className="bg-[#1E3F33] py-10" data-testid="about-footer">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-xs text-[#F9F9F6]/30">
                &copy; {new Date().getFullYear()} Dr. Neha Agarwal. All rights reserved.
              </p>
              <Link to="/" className="text-xs text-[#F9F9F6]/30 hover:text-[#F9F9F6]/60 transition-colors flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Back to Home
              </Link>
            </div>
          </div>
        </footer> */}
      </div>
    </>
  );
}
